import { useId, useState } from 'react';
import {
  Button,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@monksflow/monks-ui';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CategoryId } from '@/shared/constants/category-colors';

export interface AgentToolCategoryOption {
  value: CategoryId;
  label: string;
}

export interface AgentToolBasicInformationProps {
  toolName: string;
  toolDescription: string;
  onSaveBasicInfo?: (
    data: { name: string; description: string; category?: CategoryId },
    workspaceId?: string,
  ) => Promise<void>;
  workspaceId?: string;
  /** Read-only fallback when toolName is empty */
  fallbackName?: string;
  /** Read-only fallback when toolDescription is empty */
  fallbackDescription?: string;
  /**
   * Category shown as a third field. Both this and `categoryOptions` are needed for the field
   * to appear, so consumers that do not offer a category keep the two-field layout untouched.
   */
  category?: CategoryId;
  categoryOptions?: readonly AgentToolCategoryOption[];
}

export function AgentToolBasicInformation({
  toolName,
  toolDescription,
  onSaveBasicInfo,
  workspaceId,
  fallbackName = '',
  fallbackDescription = '',
  category,
  categoryOptions,
}: AgentToolBasicInformationProps) {
  const { t } = useTranslation('admin');

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState(toolName);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState(toolDescription);
  const [editingCategoryValue, setEditingCategoryValue] = useState(category);
  // Generated rather than fixed so two of these on one page cannot claim the same label.
  const nameFieldId = useId();
  const descriptionFieldId = useId();

  // Reset-on-workspace-change is done via `key` at the parent.

  const showCategory = !!categoryOptions && !!category;

  const handleEdit = () => {
    setEditingNameValue(toolName);
    setEditingDescriptionValue(toolDescription);
    setEditingCategoryValue(category);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only a category the user actually moved. The value shown can be inherited from a
      // narrower scope than the one being edited, so echoing it back would write that
      // inherited value as an override of its own.
      const categoryChanged = showCategory && editingCategoryValue !== category;
      await onSaveBasicInfo?.(
        {
          name: editingNameValue,
          description: editingDescriptionValue,
          category: categoryChanged ? editingCategoryValue : undefined,
        },
        workspaceId,
      );
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const displayName = toolName || fallbackName;
  const displayDescription = toolDescription || fallbackDescription;
  // Falls back to the raw value so a category outside the offered set, which the API accepts,
  // reads as itself instead of as an empty field.
  const displayCategory =
    categoryOptions?.find((option) => option.value === category)?.label ?? category ?? '';

  return (
    <div className="flex flex-col gap-4 px-6 py-5">
      <div className="flex flex-row items-center justify-between">
        <h3 className="text-xl leading-7 text-foreground">
          {t('admin.agentToolBasicInformation.basicInformation')}
        </h3>
        {!isEditing ? (
          <Button type="button" variant="outline" size="sm" onClick={handleEdit}>
            <PencilIcon />
            {t('admin.agentToolBasicInformation.edit')}
          </Button>
        ) : (
          <div className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('admin.agentToolBasicInformation.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleSave()}
              disabled={isSaving}
              className="min-w-18"
            >
              {/* Hidden from the a11y tree so the button keeps its own name. */}
              {isSaving && <Spinner aria-hidden="true" />}
              {t('admin.agentToolBasicInformation.save')}
            </Button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="flex gap-4">
          <Field className="basis-0 grow">
            <FieldLabel htmlFor={nameFieldId}>
              {t('admin.agentToolBasicInformation.toolName')}
            </FieldLabel>
            <Input
              id={nameFieldId}
              size="lg"
              value={editingNameValue}
              onChange={(e) => setEditingNameValue(e.target.value)}
              disabled={isSaving}
            />
          </Field>
          <Field className="basis-0 grow-[2]">
            <FieldLabel htmlFor={descriptionFieldId}>
              {t('admin.agentToolBasicInformation.toolDescription')}
            </FieldLabel>
            <Input
              id={descriptionFieldId}
              size="lg"
              value={editingDescriptionValue}
              onChange={(e) => setEditingDescriptionValue(e.target.value)}
              disabled={isSaving}
            />
          </Field>
          {showCategory && (
            <div className="flex basis-0 grow flex-col gap-1">
              <p className="text-xs leading-4 text-muted-foreground">
                {t('admin.agentToolBasicInformation.category')}
              </p>
              <Select
                value={editingCategoryValue}
                onValueChange={(next) => setEditingCategoryValue(next as CategoryId)}
                disabled={isSaving}
              >
                <SelectTrigger
                  size="lg"
                  className="w-full"
                  data-testid="agent-tool-category-select-trigger"
                  aria-label={t('admin.agentToolBasicInformation.category')}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-4">
          <div className="flex basis-0 grow flex-col gap-1">
            <p className="text-xs leading-4 text-muted-foreground">
              {t('admin.agentToolBasicInformation.toolName')}
            </p>
            <p className="text-sm leading-5">{displayName}</p>
          </div>
          <div className="flex basis-0 grow-[2] flex-col gap-1">
            <p className="text-xs leading-4 text-muted-foreground">
              {t('admin.agentToolBasicInformation.toolDescription')}
            </p>
            <p className="text-sm leading-5">{displayDescription}</p>
          </div>
          {showCategory && (
            <div className="flex basis-0 grow flex-col gap-1">
              <p className="text-xs leading-4 text-muted-foreground">
                {t('admin.agentToolBasicInformation.category')}
              </p>
              <p className="text-sm leading-5">{displayCategory}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
